import apiService from './api';
import { newUuid } from './evaluationQueue';
import type { ApiResponse } from '../types';
import type { StocktakePlace } from '../types/nurseryStocktake';
import type {
  ColourSettings, Paint, SchemeResponse, ColourBy, SetCodeResult, ColourHistoryRow, SubjectType,
  PaintWalk, BandsResponse, LotPainting, PaintingPayload, Band,
} from '../types/nurseryColour';

/**
 * Nursery paint colour coding: the scheme (which colours each line gets) and
 * painting (which bands have gone on the trees).
 *
 * Painting is done in the rows, where signal comes and goes, so a tap is
 * stored with its own `client_uuid` before it is sent, as stocktake counts are.
 * The server is idempotent on that id.
 */

const colourService = {
  getSettings(): Promise<ApiResponse<ColourSettings>> {
    return apiService.get('/nursery/colour-codes/settings');
  },
  updateSettings(patch: Partial<Omit<ColourSettings, 'updated_at'>>): Promise<ApiResponse<ColourSettings>> {
    return apiService.put('/nursery/colour-codes/settings', patch);
  },
  getPaints(): Promise<ApiResponse<Paint[]>> {
    return apiService.get('/nursery/colour-codes/paints');
  },
  createPaint(p: { name: string; hex: string; paint_number?: number; role?: Paint['role'] }): Promise<ApiResponse<Paint>> {
    return apiService.post('/nursery/colour-codes/paints', p);
  },
  updatePaint(id: number, p: Partial<Omit<Paint, 'id'>>): Promise<ApiResponse<Paint>> {
    return apiService.put(`/nursery/colour-codes/paints/${id}`, p);
  },
  getScheme(mode?: ColourBy): Promise<ApiResponse<SchemeResponse>> {
    return apiService.get(`/nursery/colour-codes${mode ? `?mode=${mode}` : ''}`);
  },
  build(apply: boolean, mode?: ColourBy): Promise<ApiResponse<SchemeResponse>> {
    return apiService.post('/nursery/colour-codes/build', { apply, ...(mode ? { mode } : {}) });
  },
  setCode(input: {
    subject_type: SubjectType; cultivar_id?: number | null; rootstock_id?: number | null;
    paint_1_id: number; paint_2_id?: number | null; reason?: string | null;
  }): Promise<ApiResponse<SetCodeResult>> {
    const body: Record<string, unknown> = { subject_type: input.subject_type, paint_1_id: input.paint_1_id, paint_2_id: input.paint_2_id ?? null, reason: input.reason ?? null };
    if (input.subject_type !== 'rootstock') body.cultivar_id = input.cultivar_id;
    if (input.subject_type !== 'cultivar') body.rootstock_id = input.rootstock_id ?? null;
    return apiService.put('/nursery/colour-codes', body);
  },
  clearCode(id: number, reason?: string): Promise<ApiResponse<{ id: number }>> {
    // DELETE carries its reason in the body.
    return apiService.delete(`/nursery/colour-codes/${id}`, { reason: reason ?? null });
  },
  history(): Promise<ApiResponse<ColourHistoryRow[]>> {
    return apiService.get('/nursery/colour-codes/history');
  },

  // ── Painting ──
  walkPlaces(): Promise<ApiResponse<StocktakePlace[]>> {
    return apiService.get('/nursery/colour-codes/walk/places');
  },
  walk(farmId: number, blockId: number | null): Promise<ApiResponse<PaintWalk>> {
    const q = new URLSearchParams({ farm_id: String(farmId) });
    if (blockId) q.set('block_id', String(blockId));
    return apiService.get(`/nursery/colour-codes/walk?${q}`);
  },
  paint(p: PaintingPayload): Promise<ApiResponse<{ painting: LotPainting; replayed: boolean }>> {
    return apiService.post('/nursery/colour-codes/paintings', p);
  },
  voidPainting(id: number, reason?: string): Promise<ApiResponse<{ id: number; lot_id: number; band: Band }>> {
    return apiService.post(`/nursery/colour-codes/paintings/${id}/void`, { reason: reason ?? null });
  },
  bands(lotIds: number[]): Promise<ApiResponse<BandsResponse>> {
    return apiService.post('/nursery/colour-codes/bands', { lot_ids: lotIds });
  },
};

export default colourService;

// ── The painting queue ──

const KEY = 'factree_painting_queue_v1';

export interface QueuedPainting {
  payload: PaintingPayload;
  /** Set when the server refused it; kept so the painter can see why. */
  refused?: string;
}

type Listener = () => void;
const listeners = new Set<Listener>();
const inFlight = new Set<string>();

function read(): QueuedPainting[] {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]') as QueuedPainting[]; } catch { return []; }
}
function write(items: QueuedPainting[]) {
  try { localStorage.setItem(KEY, JSON.stringify(items)); } catch { /* storage blocked: sent live only */ }
  listeners.forEach((l) => l());
}

export function subscribePaintingQueue(l: Listener): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

/** What is waiting, by lot and band. */
export function queuedPaintings(): Map<string, QueuedPainting> {
  const out = new Map<string, QueuedPainting>();
  for (const q of read()) out.set(`${q.payload.lot_id}:${q.payload.band}`, q);
  return out;
}

export function dropQueuedPainting(clientUuid: string): void {
  write(read().filter((q) => q.payload.client_uuid !== clientUuid));
}

function isNetworkError(e: unknown): boolean {
  const err = e as { offline?: boolean; status?: number } | null;
  return !!err && (err.offline === true || err.status == null || err.status === 0);
}

/**
 * Record that a band went on. Resolves with the server's record, or `null`
 * when it is queued for later. Throws only when the server refused it.
 */
export async function recordPainting(lotId: number, band: Band): Promise<LotPainting | null> {
  const payload: PaintingPayload = { lot_id: lotId, band, client_uuid: newUuid(), painted_at: new Date().toISOString() };
  write([...read().filter((q) => !(q.payload.lot_id === lotId && q.payload.band === band)), { payload }]);
  inFlight.add(payload.client_uuid);
  try {
    const res = await colourService.paint(payload);
    if (!res.success) throw new Error(res.error?.message || 'Refused by the server');
    dropQueuedPainting(payload.client_uuid);
    return res.data.painting;
  } catch (e) {
    if (isNetworkError(e)) return null;
    dropQueuedPainting(payload.client_uuid);
    throw e;
  } finally {
    inFlight.delete(payload.client_uuid);
  }
}

/** Send what is waiting. Refused taps are marked, not dropped. */
export async function flushPaintingQueue(): Promise<{ sent: number; offline: boolean }> {
  let sent = 0;
  for (const q of read()) {
    if (q.refused || inFlight.has(q.payload.client_uuid)) continue;
    try {
      const res = await colourService.paint(q.payload);
      if (!res.success) throw new Error(res.error?.message || 'Refused by the server');
      dropQueuedPainting(q.payload.client_uuid);
      sent++;
    } catch (e) {
      if (isNetworkError(e)) return { sent, offline: true };
      const reason = (e as Error)?.message || 'Refused by the server';
      write(read().map((x) => (x.payload.client_uuid === q.payload.client_uuid ? { ...x, refused: reason } : x)));
    }
  }
  return { sent, offline: false };
}
